'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useAuth } from '@/lib/auth'
import { Mail, Lock, LogIn, Leaf, Apple, Carrot, Croissant, Milk, Salad, Cherry, Wheat } from 'lucide-react'
import TwoFactorVerification from '@/components/auth/TwoFactorVerification'
import api from '@/lib/api'

export default function LoginPage() {
  const router = useRouter()
  const { login, isLoading, error, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false)
  const [tempToken, setTempToken] = useState<string | null>(null)
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [isVerifying2FA, setIsVerifying2FA] = useState(false)

  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setLocalError(null)

    try {
      const response = await api.post('/api/auth/login', { email, password })
      const data = response.data.data || response.data

      // Check if 2FA is required
      if (data.requiresTwoFactor) {
        setRequires2FA(true)
        setTempToken(data.tempToken)
        return
      }

      // Normal login flow
      await login(email, password)
      router.push('/dashboard')
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Neplatné přihlašovací údaje'
      setLocalError(errorMessage)
    }
  }

  const handle2FAVerify = async (code: string, isBackupCode: boolean) => {
    setIsVerifying2FA(true)
    setTwoFactorError(null)

    try {
      const response = await api.post('/api/twofactor/verify', {
        tempToken,
        code,
        isBackupCode
      })
      const data = response.data.data || response.data

      // Store tokens and user data
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)

      router.push('/dashboard')
    } catch (err: any) {
      setTwoFactorError(err.response?.data?.message || 'Neplatný ověřovací kód')
    } finally {
      setIsVerifying2FA(false)
    }
  }

  const handleCancel2FA = () => {
    setRequires2FA(false)
    setTempToken(null)
    setTwoFactorError(null)
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Green branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-700 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-16 left-8 w-48 h-48 bg-white/5 rounded-full"></div>
        <div className="absolute top-32 right-12 w-32 h-32 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-24 left-16 w-64 h-64 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-48 right-8 w-24 h-24 bg-white/5 rounded-full"></div>

        {/* Floating food icons - flat white style */}
        <Apple className="absolute top-20 left-24 w-8 h-8 text-white/20" />
        <Carrot className="absolute top-40 right-28 w-10 h-10 text-white/15" />
        <Croissant className="absolute bottom-40 left-20 w-9 h-9 text-white/20" />
        <Milk className="absolute bottom-20 right-20 w-8 h-8 text-white/15" />
        <Salad className="absolute top-1/2 left-16 w-10 h-10 text-white/15" />
        <Cherry className="absolute top-28 right-20 w-7 h-7 text-white/20" />
        <Wheat className="absolute bottom-32 right-32 w-9 h-9 text-white/15" />
        <Leaf className="absolute top-2/3 right-16 w-8 h-8 text-white/20" />

        {/* Center content */}
        <div className="flex flex-col items-center justify-center w-full z-10 px-12">
          <img src="/logo.png" alt="Stop Plýtvání Potravin" className="h-32 w-auto mb-6" />
          <p className="text-white/80 text-center text-base max-w-xs">
            Sdílejte jídlo, které by jinak skončilo v koši. Pomáhejte přírodě a lidem ve vašem okolí.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-3 p-2">
              <img src="/logo.png" alt="Stop Plýtvání Potravin" className="h-full w-auto" />
            </div>
          </div>

          {requires2FA ? (
            <TwoFactorVerification
              tempToken={tempToken!}
              onVerify={handle2FAVerify}
              onCancel={handleCancel2FA}
              isLoading={isVerifying2FA}
              error={twoFactorError}
            />
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Přihlášení</h2>
                <p className="text-gray-500 mt-2">Zadejte své přihlašovací údaje</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
            {(error || localError) && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error || localError}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="vas@email.cz"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Heslo
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link href="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                Zapomenuté heslo?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <LogIn className="w-5 h-5" />
              {isLoading ? 'Přihlašování...' : 'Přihlásit se'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <span className="text-gray-500">Nemáte účet? </span>
            <Link href="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              Zaregistrujte se
            </Link>
          </div>

          {/* Quick login buttons for development only */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-400 text-center mb-3">Rychlé přihlášení (dev)</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEmail('admin@stopplytvani.cz')
                    setPassword('Admin123!')
                  }}
                  className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-700 text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmail('test1@stopplytvani.cz')
                    setPassword('Test123!')
                  }}
                  className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                >
                  Test User
                </button>
              </div>
            </div>
          )}
            </>
          )}

          <p className="mt-8 text-center text-xs text-gray-400">
            © 2025 Stop Plýtvání Potravin. Všechna práva vyhrazena.
          </p>
        </div>
      </div>
    </div>
  )
}
